import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { getSessionStorageValue, setSessionStorageValue, STORAGE_KEYS } from '../../Utils/SessionStorage';
import { APPOINTMENT_TYPES } from '../../Utils/bookingConfig';

const AppointmentContext = createContext();

export const AppointmentProvider = ({ children }) => {
  const [appointmentTypeID, setAppointmentTypeIDState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.APPOINTMENT_TYPE) || null
  );

  const [membership, setMembershipState] = React.useState(() => {
    const stored = getSessionStorageValue(STORAGE_KEYS.MEMBERSHIP);
    const initial = stored ?? true;
    if (stored === null) {
      setSessionStorageValue(STORAGE_KEYS.MEMBERSHIP, initial);
    }
    return initial;
  });

  // Actual membership status from the OTP API (is_membership field).
  // Used for booking membership and eligibility fields, independently of the
  // UI price display (which always uses member rates).
  const [actualMembership, setActualMembershipState] = React.useState(false);
  const setActualMembership = useCallback((isMember) => {
    setActualMembershipState(isMember === true || isMember === 1 || isMember === '1');
  }, []);

  const setAppointmentTypeID = useCallback((typeID) => {
    setAppointmentTypeIDState(typeID);
    if (typeID === null) {
      setSessionStorageValue(STORAGE_KEYS.APPOINTMENT_TYPE, null);
    } else {
      setSessionStorageValue(STORAGE_KEYS.APPOINTMENT_TYPE, typeID);
    }
  }, []);

  const setMembership = useCallback((isMember) => {
    setMembershipState(isMember);
    setSessionStorageValue(STORAGE_KEYS.MEMBERSHIP, isMember);
  }, []);

  // Derived selectors (memoized)
  const isSingleBooking = useMemo(
    () => Number(appointmentTypeID) === APPOINTMENT_TYPES.SINGLE,
    [appointmentTypeID]
  );

  const isGroupBooking = useMemo(
    () => Number(appointmentTypeID) === APPOINTMENT_TYPES.GROUP,
    [appointmentTypeID]
  );

  const isMemberFlowBooking = useMemo(
    () => isSingleBooking || isGroupBooking,
    [isSingleBooking, isGroupBooking]
  );

  const value = useMemo(
    () => ({
      appointmentTypeID,
      setAppointmentTypeID,
      membership,
      setMembership,
      actualMembership,
      setActualMembership,
      isSingleBooking,
      isGroupBooking,
      isMemberFlowBooking,
      APPOINTMENT_TYPES,
    }),
    [appointmentTypeID, setAppointmentTypeID, membership, setMembership, actualMembership, setActualMembership, isSingleBooking, isGroupBooking, isMemberFlowBooking]
  );

  return (
    <AppointmentContext.Provider value={value}>
      {children}
    </AppointmentContext.Provider>
  );
};

export const useAppointment = () => {
  const context = useContext(AppointmentContext);
  if (!context) {
    throw new Error('useAppointment must be used within AppointmentProvider');
  }
  return context;
};

export default AppointmentContext;
